import { calculateStaffScale } from '../editor/staffScaleModel.js';
import { pitchToStaffIndex } from '../widgets/staff/pitchMath.js';

export const HARMONIC_MINOR_NUMERALS = ['i', 'ii°', 'III+', 'iv', 'V', 'VI', 'vii°', 'i'];
export const SCALE_DEGREE_NAMES = ['tonic', 'supertonic', 'mediant', 'subdominant', 'dominant', 'submediant', 'leading tone', 'tonic'];
export const TRIAD_SECTION_POINTS = { keySignature: 1, triads: 2, romanNumerals: 2, degreeNames: 4 };

export function harmonicMinorTriadKey(config = {}) {
  const settings = config.harmonicMinorTriads || {};
  const tonic = settings.tonic || 'C';
  const clef = config.clef || 'treble';
  const octave = Number(settings.octave ?? (clef === 'bass' ? 2 : 4));
  const scale = calculateStaffScale(tonic, 'harmonicMinor', clef, octave);
  const notes = {}, accidentals = {};
  for (let slot = 1; slot <= 8; slot++) {
    notes[slot] = [];
    accidentals[slot] = {};
    for (const [index, offset] of [0, 2, 4].entries()) {
      const degree = slot - 1 + offset;
      const source = degree % 7 + 1;
      const raw = scale.givenNotes[source].pitches[0];
      const [, letter, embedded, baseOctave] = raw.match(/^([A-G])([#b]*)(\d+)$/);
      notes[slot].push(letter + (Number(baseOctave) + Math.floor(degree / 7)));
      accidentals[slot][index] = scale.accidentals[source] ?? embedded;
    }
  }
  if (Object.values(notes).flat().some(pitch => {
    const index = pitchToStaffIndex(pitch, clef);
    return index < -5 || index > 13;
  })) throw Error('These triads exceed the input range. Choose a starting octave closer to the staff.');
  const points = { ...TRIAD_SECTION_POINTS, ...settings.points };
  const weights = Object.values(points);
  if (weights.some(p => !Number.isFinite(p) || p < 0)) throw Error('Section points must be nonnegative numbers.');
  return { tonic, clef, octave, notes, accidentals, signature: scale.keySignatureAccidentals,
    romanNumerals: HARMONIC_MINOR_NUMERALS, degreeNames: SCALE_DEGREE_NAMES,
    points, totalPoints: weights.reduce((sum, p) => sum + p, 0) };
}

export function harmonicMinorTriadStaffConfig(config) {
  const key = harmonicMinorTriadKey(config);
  const displayed = key.tonic.toLowerCase().replaceAll('#', '♯').replaceAll('b', '♭');
  return { ...config, points: key.totalPoints, inputMode: 'placeNotes', measures: 8, slotCount: 8,
    slotsPerBar: 1, maxNotesPerSlot: 3, allowedSymbols: ['whole_note'],
    allowLedgerLines: true, allowKeySignatureInput: true, keySignatureInputPosition: 'start',
    frontKeySignatureWidth: 85, scaleLabel: `${displayed} minor, harmonic`, scaleLabelPosition: 'above',
    staffInputPolicy: { allowNoteInput: true, allowAccidentalInput: true, allowHalfStepInput: false, accidentalInputMode: 'palette' },
    grading: { notes: key.notes, accidentals: key.accidentals }, rules: { requiredSymbol: 'whole_note' } };
}
